// Fichier: pages/MentionsLegales.js
import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ThemeProvider as CustomThemeProvider, useTheme } from '../context/ThemeContext';

// Style global unique - exactement comme dans HomePage.js
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Poppins', sans-serif;
    transition: background-color 0.3s ease, 
                color 0.3s ease, 
                border-color 0.3s ease, 
                box-shadow 0.3s ease;
    -webkit-tap-highlight-color: transparent; /* Supprime le highlight bleu sur mobile */
    -webkit-touch-callout: none; /* Désactive le menu contextuel sur iOS */
    -webkit-user-select: none; /* Empêche la sélection de texte sur WebKit */
    user-select: none; /* Standard syntax */
  }
  
  html, body {
    overflow-x: hidden;
    scroll-behavior: smooth;
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
  
  button {
    cursor: pointer;
    border: none;
    outline: none;
  }

  /* Permet la sélection de texte pour les paragraphes et autres contenus textuels */
  p, span, div, label {
    user-select: text;
  }
`;

const MentionsLegalesSection = styled.section`
  padding: 10rem 5% 4rem;
  background-color: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text};
  
  span {
    color: ${props => props.theme.colors.accent};
  }
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.accentGradient};
    margin-top: 1rem;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Paragraph = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.secondaryText};
`;

const List = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ListItem = styled.li`
  line-height: 1.7;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondaryText};
`;

const Link = styled.a`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }
`;

// Wrapper avec le thème, similaire à HomePageWrapper dans HomePage.js
const MentionsLegalesWrapper = () => {
    const { theme } = useTheme();
    const [isScrolled, setIsScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <Helmet>
                    <title>Mentions légales - Rankeed | Création de sites web</title>
                    <meta name="description" content="Mentions légales de Rankeed, agence de création de sites web professionnels." />
                    <link rel="canonical" href="https://rankeed.fr/mentions-legales" />
                </Helmet>

                <Header
                    isScrolled={isScrolled}
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    isStandalonePage={true}
                />

                <MentionsLegalesContent />

                <Footer />
            </>
        </ThemeProvider>
    );
};

// Contenu principal de la page
const MentionsLegalesContent = () => {
    return (
        <MentionsLegalesSection>
            <Container>
                <PageTitle>Mentions <span>légales</span></PageTitle>

                <Section>
                    <SectionTitle>1. Éditeur du site</SectionTitle>
                    <Paragraph>
                        Le présent site web est édité par :
                    </Paragraph>
                    <List>
                        <ListItem><strong>Sumfy</strong> – Micro-entreprise de Arthur Diard</ListItem>
                        <ListItem><strong>Nom commercial :</strong> Rankeed</ListItem>
                        <ListItem><strong>SIRET :</strong> 931 242 770 00010</ListItem>
                        <ListItem><strong>Adresse email :</strong> <Link href="mailto:contact.sumfy@gmail.com">contact.sumfy@gmail.com</Link></ListItem>
                        <ListItem><strong>Responsable de la publication :</strong> Arthur Diard</ListItem>
                        <ListItem><strong>Statut juridique :</strong> Micro-entreprise</ListItem>
                        <ListItem><strong>Lieu d'exercice :</strong> France</ListItem>
                    </List>
                </Section>

                <Section>
                    <SectionTitle>2. Hébergeur</SectionTitle>
                    <Paragraph>
                        Le site est hébergé par :
                    </Paragraph>
                    <List>
                        <ListItem><strong>Hostinger International Ltd.</strong></ListItem>
                        <ListItem><strong>Adresse :</strong> 61 Lordou Vironos Street, 6023 Larnaca, Chypre</ListItem>
                        <ListItem><strong>Site :</strong> <Link href="https://www.hostinger.fr/" target="_blank" rel="noopener noreferrer">https://www.hostinger.fr/</Link></ListItem>
                    </List>
                </Section>

                <Section>
                    <SectionTitle>3. Propriété intellectuelle</SectionTitle>
                    <Paragraph>
                        Tous les contenus (textes, images, logos, etc.) présents sur ce site sont la propriété exclusive de Sumfy, sauf mention contraire. Toute reproduction, distribution ou utilisation sans autorisation écrite est strictement interdite.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>4. Activité</SectionTitle>
                    <Paragraph>
                        Rankeed est un service de création de sites web modernes, rapides et optimisés, proposé par la micro-entreprise Sumfy.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>5. Contact</SectionTitle>
                    <Paragraph>
                        Pour toute question ou signalement : <Link href="mailto:contact.sumfy@gmail.com">contact.sumfy@gmail.com</Link>
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>6. Protection des données personnelles</SectionTitle>
                    <Paragraph>
                        Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition aux données vous concernant.
                    </Paragraph>
                    <Paragraph>
                        Pour exercer ce droit, veuillez nous contacter par email à l'adresse suivante : <Link href="mailto:contact.sumfy@gmail.com">contact.sumfy@gmail.com</Link>
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>7. Cookies</SectionTitle>
                    <Paragraph>
                        Ce site n'utilise que les cookies techniques nécessaires à son bon fonctionnement.
                        Aucun cookie de profilage ou publicitaire n'est utilisé sans votre consentement explicite.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>8. Droit applicable</SectionTitle>
                    <Paragraph>
                        Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront compétents.
                    </Paragraph>
                </Section>
            </Container>
        </MentionsLegalesSection>
    );
};

// Composant avec le context ThemeProvider
const MentionsLegalesPage = () => {
    return (
        <CustomThemeProvider>
            <MentionsLegalesWrapper />
        </CustomThemeProvider>
    );
};

export default MentionsLegalesPage;